import ThemedElement from "shared/components/ThemedElement";
import React from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  LinearProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import CancelIcon from "@mui/icons-material/Cancel";

import ConfirmacionImportacionTags from "./ConfirmacionEnvioTags";
import { useFormImportacionTags } from "../../hooks/useFormEnvioTags";

const colors = {
  primary: "#09702dff",
  success: "#27ae60",
  error: "#e74c3c",
  info: "#2980b9",
  background: "#f8f9fa",
  border: "#dfe6e9",
  warning: "#ed6c02",
};

const FormImportacionTags = ({ onImportSuccess, onError, onBusyChange }) => {
  const {
    selectedFile,
    importing,
    importResult,
    showConfirmModal,
    validationErrors,
    validating,
    snackbar,
    loading: userLoading,
    importProgress,
    duplicateInFile,
    duplicateInDatabase,
    validationDetails,
    getCurrentUsername,
    handleFileChange,
    handleImport,
    handleCloseModal,
    clearSelectedFile,
    handleCloseSnackbar,
    setSnackbar,
  } = useFormImportacionTags();

  React.useEffect(() => {
    onBusyChange?.(importing || validating);
  }, [importing, validating, onBusyChange]);
  const [showErrorDetails, setShowErrorDetails] = React.useState(true);

  const onFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      await handleFileChange(file, event);
    }
  };

  const onImportClick = async () => {
    await handleImport(onImportSuccess, onError);
  };

  const handleDownloadTemplate = () => {
    try {
      const templateUrl = "/templates/plantilla_tags.xlsx";

      const link = document.createElement("a");
      link.href = templateUrl;
      link.download = "plantilla_tags.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSnackbar({
        open: true,
        message: "Descargando plantilla de ejemplo...",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al descargar plantilla:", error);
      setSnackbar({
        open: true,
        message: "Error al descargar la plantilla. Contacte al administrador.",
        severity: "error",
      });
    }
  };

  if (userLoading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <MDTypography variant="body2" ml={2} color="text.secondary">
          Cargando información del usuario...
        </MDTypography>
      </MDBox>
    );
  }

  // Determinar si el archivo tiene errores
  const hasErrors =
    validationErrors.length > 0 || duplicateInFile.length > 0 || duplicateInDatabase.length > 0;
  const isValidFile = selectedFile && !hasErrors && !validating;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: `2px solid ${colors.border}`,
          backgroundColor: "white",
          mb: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: colors.primary,
            color: "white",
            p: 3,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <CloudUploadIcon fontSize="large" />
              <Box>
                <MDTypography variant="h5" fontWeight="bold" color="white">
                  Importar Tags desde Excel
                </MDTypography>
              </Box>
            </Box>

            <MDButton
              variant="outlined"
              onClick={handleDownloadTemplate}
              startIcon={<DownloadIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                color: "white",
                borderColor: "rgba(255,255,255,0.5)",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Descargar Plantilla
            </MDButton>
          </Box>
        </Box>

        <Box px={3} py={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: `2px dashed ${
                    isValidFile
                      ? colors.success
                      : hasErrors && selectedFile
                      ? colors.error
                      : validationErrors.length > 0 && !selectedFile
                      ? colors.error
                      : colors.border
                  }`,
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  backgroundColor: isValidFile
                    ? `${colors.success}08`
                    : hasErrors && selectedFile
                    ? `${colors.error}08`
                    : validationErrors.length > 0 && !selectedFile
                    ? `${colors.error}08`
                    : colors.background,
                  transition: "all 0.2s",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: isValidFile
                      ? colors.success
                      : hasErrors && selectedFile
                      ? colors.error
                      : colors.info,
                    backgroundColor: isValidFile
                      ? `${colors.success}10`
                      : hasErrors && selectedFile
                      ? `${colors.error}10`
                      : "#fff",
                  },
                }}
                onClick={() => {
                  if (!importing && !validating) document.getElementById("file-input")?.click();
                }}
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={onFileSelect}
                  style={{ display: "none" }}
                  disabled={validating || importing}
                />
                {validating ? (
                  <>
                    <CircularProgress size={48} sx={{ mb: 1 }} />
                    <MDTypography variant="h6" color={colors.info} gutterBottom>
                      Validando archivo...
                    </MDTypography>
                  </>
                ) : selectedFile ? (
                  <>
                    {isValidFile ? (
                      <CheckCircleIcon sx={{ fontSize: 48, color: colors.success, mb: 1 }} />
                    ) : (
                      <ErrorIcon sx={{ fontSize: 48, color: colors.error, mb: 1 }} />
                    )}
                    <MDTypography
                      variant="h6"
                      color={isValidFile ? colors.success : colors.error}
                      gutterBottom
                    >
                      {isValidFile ? "Archivo válido" : "Archivo inválido"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      {selectedFile.name}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      ({Math.round(selectedFile.size / 1024)} KB)
                    </MDTypography>
                    <MDButton
                      variant="text"
                      size="small"
                      color="error"
                      disabled={validating || importing}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSelectedFile();
                      }}
                      sx={{ mt: 1 }}
                    >
                      Cambiar archivo
                    </MDButton>
                  </>
                ) : (
                  <>
                    <DescriptionIcon
                      sx={{
                        fontSize: 48,
                        color: validationErrors.length > 0 ? colors.error : colors.info,
                        mb: 1,
                      }}
                    />
                    <MDTypography
                      variant="h6"
                      color={validationErrors.length > 0 ? colors.error : colors.info}
                      gutterBottom
                    >
                      {validationErrors.length > 0
                        ? "Archivo inválido"
                        : "Seleccionar archivo Excel"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Haga clic para buscar un archivo .xlsx o .xls
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      Columnas requeridas: Reverse Number, Number Below the QR, Code QR, ESTADO,
                      LOTE, ESTADO TAGS (TODAS son obligatorias)
                    </MDTypography>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Mostrar errores de validación */}
          {validationErrors.length > 0 && selectedFile && (
            <Collapse in={showErrorDetails}>
              <Box
                sx={{
                  mt: 2,
                  bgcolor: "#ffebee",
                  borderRadius: 2,
                  border: "1px solid #ef9a9a",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#c62828",
                    color: "white",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <ErrorIcon />
                    <MDTypography variant="subtitle2" fontWeight="bold" color="white">
                      Errores de validación ({validationErrors.length})
                    </MDTypography>
                  </Box>
                </Box>
                <Box sx={{ p: 2, maxHeight: 300, overflow: "auto" }}>
                  <List dense disablePadding>
                    {validationErrors.slice(0, 10).map((err, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CancelIcon sx={{ fontSize: 16, color: colors.error }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={err}
                          primaryTypographyProps={{
                            variant: "caption",
                            sx: { color: "#c62828", fontFamily: "monospace" },
                          }}
                        />
                      </ListItem>
                    ))}
                    {validationErrors.length > 10 && (
                      <ListItem>
                        <ListItemText
                          primary={`... y ${validationErrors.length - 10} errores más`}
                          primaryTypographyProps={{
                            variant: "caption",
                            sx: { color: "#c62828", fontStyle: "italic" },
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                </Box>
              </Box>
            </Collapse>
          )}

          {/* Mostrar duplicados en archivo */}
          {duplicateInFile.length > 0 && selectedFile && (
            <Collapse in={showErrorDetails}>
              <Box
                sx={{
                  mt: 2,
                  bgcolor: "#fff3e0",
                  borderRadius: 2,
                  border: "1px solid #ffe082",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    bgcolor: "#ed6c02",
                    color: "white",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <WarningIcon />
                  <MDTypography variant="subtitle2" fontWeight="bold" color="white">
                    Duplicados dentro del archivo ({duplicateInFile.length})
                  </MDTypography>
                </Box>
                <Box sx={{ p: 2, maxHeight: 200, overflow: "auto" }}>
                  <List dense disablePadding>
                    {duplicateInFile.map((dup, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <WarningIcon sx={{ fontSize: 16, color: "#ed6c02" }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Fila ${dup.row}: ${dup.type} "${dup.value}" duplicado`}
                          primaryTypographyProps={{
                            variant: "caption",
                            sx: { color: "#ed6c02", fontFamily: "monospace" },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Collapse>
          )}

          {/* Mostrar mensaje cuando el archivo es válido */}
          {isValidFile && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#e8f5e9",
                borderRadius: 2,
                border: "1px solid #a5d6a7",
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleIcon sx={{ color: colors.success }} />
                <MDTypography variant="subtitle2" fontWeight="bold" color={colors.success}>
                  Validación exitosa
                </MDTypography>
              </Box>
              <MDTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                El archivo está listo para ser importado.
              </MDTypography>
            </Box>
          )}

          <Box
            sx={{
              mt: 4,
              mb: 2,
              p: 2.5,
              bgcolor: "#fff8e1",
              borderRadius: 2,
              border: "1px solid #ffe082",
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <HelpOutlineIcon sx={{ fontSize: 22, color: colors.warning }} />
              <MDTypography variant="subtitle1" fontWeight="bold" color={colors.warning}>
                Instrucciones para la importación
              </MDTypography>
            </Box>

            <MDTypography variant="body2" color="text.secondary" component="div">
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                <li>El archivo debe ser tipo Excel (.xlsx o .xls)</li>
                <li>
                  Debe contener las columnas: <strong>Reverse Number</strong>,{" "}
                  <strong>Number Below the QR</strong>, <strong>Code QR</strong>,{" "}
                  <strong>ESTADO</strong>, <strong>LOTE</strong>, <strong>ESTADO TAGS</strong>{" "}
                  (TODAS son obligatorias)
                </li>
                <li>Los tags se importarán con toda su información completa</li>
                <li>El archivo será validado automáticamente al seleccionarlo</li>
                <li>
                  La primera fila debe contener los encabezados:{" "}
                  <strong>
                    Reverse Number, Number Below the QR, Code QR, ESTADO, LOTE, ESTADO TAGS
                  </strong>
                </li>
                <li>Las filas siguientes contienen los datos de cada tag</li>
                <li>
                  La columna <strong>Code QR</strong> es{" "}
                  <ThemedElement component="strong" style={{ color: colors.error }}>
                    OBLIGATORIA
                  </ThemedElement>{" "}
                  y debe ser única
                </li>
                <li>
                  <strong>No se permiten tags duplicados</strong> (ni dentro del mismo archivo ni
                  que ya existan en el sistema)
                </li>
                <li>
                  <strong>Recomendación:</strong> Descargue la plantilla de ejemplo para asegurar el
                  formato correcto
                </li>
              </ul>
            </MDTypography>

            <Box
              sx={{
                mt: 2,
                pt: 1.5,
                borderTop: "1px dashed #ffe082",
              }}
            >
              <MDTypography variant="caption" color="text.secondary">
                <strong>Ejemplo de fila válida:</strong>{" "}
                <code style={{ backgroundColor: "#fff", padding: "2px 6px", borderRadius: 4 }}>
                  123456789, 987654321, QR-CODE-001, ACTIVO, LOTE-001, DISPONIBLE
                </code>
              </MDTypography>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <MDButton
              variant="contained"
              fullWidth
              disabled={!isValidFile || importing || validating}
              onClick={onImportClick}
              size="large"
              sx={{
                py: 1.5,
                fontSize: "1rem",
                color: "#ffffffff",
                fontWeight: "bold",
                borderRadius: 1,
                bgcolor: isValidFile ? colors.success : colors.border,
                "&:hover": {
                  bgcolor: isValidFile ? "#1e8449" : colors.border,
                },
                "&:disabled": {
                  bgcolor: colors.border,
                  color: "#ffffffff",
                },
              }}
            >
              {importing ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1.5, color: "#ffffffff" }} />
                  Procesando...
                </>
              ) : validating ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1.5, color: "#ffffffff" }} />
                  Validando...
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ mr: 1.5, color: "#ffffffff" }} />
                  Procesar Tags
                </>
              )}
            </MDButton>
          </Box>
        </Box>
      </Paper>

      <ConfirmacionImportacionTags
        open={showConfirmModal}
        onClose={handleCloseModal}
        importing={importing}
        importResult={importResult}
        fileName={selectedFile?.name}
        importProgress={importProgress}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "info" ? 3000 : 6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{
            width: "100%",
            minWidth: "300px",
            backgroundColor:
              snackbar.severity === "success"
                ? "#f0fff4"
                : snackbar.severity === "error"
                ? "#fff5f5"
                : snackbar.severity === "info"
                ? "#e3f2fd"
                : "#fff8e1",
            color:
              snackbar.severity === "success"
                ? "#276749"
                : snackbar.severity === "error"
                ? "#c53030"
                : snackbar.severity === "info"
                ? "#0b4f6c"
                : "#856404",
            "& .MuiAlert-icon": {
              color:
                snackbar.severity === "success"
                  ? "#276749"
                  : snackbar.severity === "error"
                  ? "#c53030"
                  : snackbar.severity === "info"
                  ? "#0b4f6c"
                  : "#856404",
            },
            border:
              snackbar.severity === "success"
                ? "1px solid #c6f6d5"
                : snackbar.severity === "error"
                ? "1px solid #fed7d7"
                : snackbar.severity === "info"
                ? "1px solid #b3e5fc"
                : "1px solid #ffe082",
          }}
        >
          <MDTypography variant="body1" fontWeight="bold">
            {snackbar.severity === "success"
              ? "¡Éxito!"
              : snackbar.severity === "error"
              ? "Error"
              : snackbar.severity === "info"
              ? "Información"
              : "Aviso"}
          </MDTypography>
          <MDTypography variant="body2">{snackbar.message}</MDTypography>
          {snackbar.severity === "info" && importProgress > 0 && importProgress < 100 && (
            <LinearProgress
              variant="determinate"
              value={importProgress}
              sx={{ mt: 1, height: 4, borderRadius: 2 }}
            />
          )}
        </Alert>
      </Snackbar>
    </>
  );
};

FormImportacionTags.propTypes = {
  onBusyChange: PropTypes.func,
  onImportSuccess: PropTypes.func,
  onError: PropTypes.func,
};

FormImportacionTags.defaultProps = {
  onBusyChange: undefined,
  onImportSuccess: null,
  onError: null,
};

export default FormImportacionTags;
